import { Hammer, UploadCloud } from 'lucide-react';
import { RubyGemMark } from './RubyGemMark';

export type StageStatus = 'success' | 'failure' | 'running' | 'none';

export type PipelineState =
  | 'idle'
  | 'building'
  | 'build-failed'
  | 'built'
  | 'deploying'
  | 'deploy-failed'
  | 'deployed';

/**
 * Derive the canonical pipeline state from build + deploy stage statuses.
 * Edge rule: deploy is only meaningful once build has succeeded — until then
 * the state is driven entirely by the build stage.
 */
export function derivePipelineState(build: StageStatus, deploy: StageStatus): PipelineState {
  if (build === 'running') return 'building';
  if (build === 'failure') return 'build-failed';
  if (build !== 'success') return 'idle';
  if (deploy === 'running') return 'deploying';
  if (deploy === 'failure') return 'deploy-failed';
  if (deploy === 'success') return 'deployed';
  return 'built';
}

const BUILD_ICON_COLOR: Record<StageStatus, string> = {
  success: 'text-green-400',
  failure: 'text-red-400',
  running: 'text-yellow-400',
  none: 'text-stone-600',
};

function deployIconColor(state: PipelineState): string {
  switch (state) {
    case 'built': return 'text-blue-400';
    case 'deploying': return 'text-yellow-400';
    case 'deploy-failed': return 'text-red-400';
    case 'deployed': return 'text-green-400';
    default: return 'text-stone-600';
  }
}

function arrowStyle(state: PipelineState): { color: string; dashed: boolean; flow: boolean } {
  switch (state) {
    case 'idle': return { color: '#404040', dashed: true, flow: false };
    case 'building': return { color: '#facc15', dashed: false, flow: false };
    case 'build-failed': return { color: '#f87171', dashed: false, flow: false };
    case 'built': return { color: '#60a5fa', dashed: false, flow: false };
    case 'deploying': return { color: '#facc15', dashed: false, flow: true };
    case 'deploy-failed': return { color: '#f87171', dashed: false, flow: false };
    case 'deployed': return { color: '#4ade80', dashed: false, flow: false };
  }
}

function timeAgo(iso?: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

export function PipelineStatus({
  buildStatus,
  deployStatus,
  env,
  regionLabel,
  deployedAt,
}: {
  buildStatus: StageStatus;
  deployStatus: StageStatus;
  env: 'dev' | 'prod';
  regionLabel: string;
  deployedAt?: string | null;
}) {
  const state = derivePipelineState(buildStatus, deployStatus);
  const arrow = arrowStyle(state);
  const buildRunning = buildStatus === 'running';
  const deployRunning = state === 'deploying';
  const accent = env === 'dev' ? 'bg-emerald-500' : 'bg-rose-500';
  const ago = timeAgo(deployedAt);

  return (
    <div className="flex items-center gap-1">
      {/* Env accent bar */}
      <div className={`w-0.5 h-5 rounded-full ${accent} shrink-0`} />
      <span className="text-[8px] text-stone-500 w-[52px] shrink-0 font-mono truncate">{regionLabel}</span>

      {/* Build stage */}
      <div className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${buildRunning ? 'glow-yellow' : ''}`}>
        {buildRunning ? <RubyGemMark size={10} drawing /> : <Hammer className={`w-2.5 h-2.5 ${BUILD_ICON_COLOR[buildStatus]}`} />}
      </div>

      {/* Arrow */}
      <svg width="28" height="8" viewBox="0 0 28 8" className="shrink-0">
        <line
          x1="1" y1="4" x2="22" y2="4"
          stroke={arrow.color}
          strokeWidth="1"
          strokeDasharray={arrow.dashed ? '2 2' : 'none'}
          strokeOpacity={arrow.dashed ? 0.4 : 0.7}
        />
        <polygon points="21,2 26,4 21,6" fill={arrow.color} opacity={arrow.dashed ? 0.3 : 0.8} />
        {arrow.flow && <circle cx="2" cy="4" r="1.5" fill="#facc15" className="arrow-dot-sm" opacity="0" />}
      </svg>

      {/* Deploy stage */}
      <div className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${deployRunning ? 'glow-yellow' : ''}`}>
        {deployRunning ? <RubyGemMark size={10} drawing /> : <UploadCloud className={`w-2.5 h-2.5 ${deployIconColor(state)}`} />}
      </div>

      {/* Deployed time */}
      {ago ? (
        <span
          className={`text-[8px] font-mono w-[48px] shrink-0 truncate ${
            state === 'deploy-failed' ? 'text-red-400/70' : state === 'deployed' ? 'text-stone-500' : 'text-stone-600'
          }`}
          title={deployedAt || ''}
        >
          {ago}
        </span>
      ) : (
        <span className="text-[8px] font-mono w-[48px] shrink-0 text-stone-700">—</span>
      )}
    </div>
  );
}
